// client/src/components/TransfertFid.js

import React, { useEffect, useMemo, useState, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc,
  runTransaction,
  arrayUnion,
} from 'firebase/firestore';
import { firestore, storage } from '../../firebase';
import { ref, getDownloadURL, listAll, getMetadata } from 'firebase/storage';
import { QRCodeCanvas } from 'qrcode.react';
import { FaArrowLeft, FaGift, FaCheckCircle, FaTimesCircle, FaCheck, FaRegCopy } from 'react-icons/fa';
import { AuthContext } from '../../AuthContext';

// Active/Désactive la finalisation côté client (si CF côté serveur, mets false)
const USE_CLIENT_FINALIZE = true;

// Cache simple logos
const logoCache = new Map();

const MerchantLogo = ({ merchantId, size = 64, style }) => {
  const [logoUrl, setLogoUrl] = useState(logoCache.get(merchantId) ?? null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!merchantId) return;

    const cached = logoCache.get(merchantId);
    if (cached !== undefined) {
      setLogoUrl(cached);
      return;
    }

    (async () => {
      try {
        const logoRef = ref(storage, `${merchantId}/photo-profil/`);
        const logoList = await listAll(logoRef);
        if (logoList.items.length > 0) {
          const withMeta = await Promise.all(
            logoList.items.map(async (item) => {
              const metadata = await getMetadata(item);
              const timeCreated = metadata.timeCreated ? new Date(metadata.timeCreated) : new Date(0);
              return { item, timeCreated };
            })
          );
          withMeta.sort((a, b) => b.timeCreated - a.timeCreated);
          const latest = withMeta[0];
          const url = await getDownloadURL(latest.item);
          logoCache.set(merchantId, url);
          if (mountedRef.current) setLogoUrl(url);
        } else {
          logoCache.set(merchantId, null);
          if (mountedRef.current) setLogoUrl(null);
        }
      } catch (e) {
        console.error('Logo merchant:', e);
      }
    })();

    return () => { mountedRef.current = false; };
  }, [merchantId]);

  return logoUrl ? (
    <img
      src={logoUrl}
      alt="Logo du commerçant"
      loading="lazy"
      style={{ width: size, height: size, objectFit: 'cover', borderRadius: '50%', border: '1px solid var(--eb-border)', flexShrink: 0, ...style }}
    />
  ) : (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--eb-surface-2)', border: '1px solid var(--eb-border)', flexShrink: 0, ...style }} />
  );
};

const StepIndicator = ({ step }) => {
  const steps = [
    { id: 1, label: 'Entreprise' },
    { id: 2, label: 'Détails' },
    { id: 3, label: 'Récap' },
    { id: 4, label: 'QR (5 min)' },
  ];
  return (
    <ol className="tf-steps" aria-label="Étapes du transfert">
      {steps.map(s => {
        const active = s.id === step;
        const passed = s.id < step;
        return (
          <li key={s.id} className={`tf-step${active ? ' is-active' : ''}${passed ? ' is-done' : ''}`} aria-current={active ? 'step' : undefined}>
            <span className="tf-step__dot">{passed ? <FaCheck size={11} /> : s.id}</span>
            <span className="tf-step__label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
};

const TransfertFid = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useContext(AuthContext);

  // Base de redirection selon le rôle
  const goTarget = userRole === 'commercant' ? '/merchantfid' : '/clientfid';
  const viewerBase = userRole === 'commercant' ? '/merchantfidviewer' : '/clientfidviewer';
  const goViewer = (merchantId) =>
    merchantId ? `${viewerBase}/${encodeURIComponent(merchantId)}` : goTarget;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const toUid = params.get('to');          // compat ancien flux
  const offerParam = params.get('offer');  // ID d’offre (QR scanné par le receveur)
  const modeParam = params.get('mode');    // "claim" pour le receveur

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const [receiverPseudo, setReceiverPseudo] = useState(toUid ? 'anonyme' : '—');
  const [error, setError] = useState('');

  const [step, setStep] = useState(1);

  const [merchants, setMerchants] = useState([]); // [{merchantId, pseudo, type, multiple, points, compteur}]
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [justSelected, setJustSelected] = useState(false);
  const [kind, setKind] = useState('points'); // points | passages
  const [qty, setQty] = useState(1);

  // Offre QR
  const [offerId, setOfferId] = useState(null);
  const [offerExpireAt, setOfferExpireAt] = useState(null);
  const [leftMs, setLeftMs] = useState(0);
  // eslint-disable-next-line
  const [waitingDone, setWaitingDone] = useState(false);

  // Overlays
  const [sending, setSending] = useState(false);
  const [okOverlay, setOkOverlay] = useState(false);
  const [koOverlay, setKoOverlay] = useState(false);

  // Ref pour nettoyer le listener d'offre créé côté émetteur
  const offerUnsubRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Auth + données
  useEffect(() => {
    const auth = getAuth();
    const unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser) {
          setError('Veuillez vous connecter.');
          setLoading(false);
          return;
        }
        setMe(currentUser);

        if (toUid) {
          try {
            const uSnap = await getDoc(doc(firestore, 'users', toUid));
            setReceiverPseudo(uSnap.exists() ? (uSnap.data()?.pseudo || 'anonyme') : 'anonyme');
          } catch {
            setReceiverPseudo('anonyme');
          }
        }

        // Mode receveur via QR
        if (offerParam && modeParam === 'claim') {
          setStep(4);
          setWaitingDone(true);

          // Écoute unique de l'offre (succès / expiré)
          const unsub = onSnapshot(doc(firestore, 'transfers', offerParam), (snap) => {
            const d = snap.data();
            if (!d) { setError('Offre introuvable'); return; }

            const isExpired = d.status === 'expired' || (d.expiresAtMs && Date.now() > d.expiresAtMs);
            if (isExpired) {
              setError('Offre expirée.');
              setWaitingDone(false);
            }

            if (d.expiresAtMs && !offerExpireAt) {
              setOfferExpireAt(d.expiresAtMs);
            }

            if (d.status === 'done') {
              setOkOverlay(true);
              setTimeout(() => {
                setOkOverlay(false);
                // 🔁 Redirection vers le viewer avec le merchantId
                navigate(goViewer(d.merchantId));
              }, 1100);
            }
          });

          // cleanup de cet abonnement au démontage/route change
          return () => unsub();
        }

        // Mode émetteur : charger les comptes fidélité
        const fidCol = collection(firestore, 'fidelity');
        const fidSnap = await getDocs(fidCol);

        const tasks = fidSnap.docs.map(async (mDoc) => {
          try {
            const mId = mDoc.id;
            const mData = mDoc.data() || {};
            const type = mData.Type || 'Fidbycount';
            const multiple = Number(mData.multiple || 0);

            const [clientSnap, userSnap] = await Promise.all([
              getDoc(doc(firestore, 'fidelity', mId, 'list', currentUser.uid)),
              getDoc(doc(firestore, 'users', mId))
            ]);

            if (!clientSnap.exists()) return null;
            const c = clientSnap.data() || {};
            const compteur = Number(c.compteur || 0);
            const points = Number(c.points || 0);
            if (compteur <= 0 && points <= 0) return null;

            const merchantPseudo = userSnap.exists() ? (userSnap.data()?.pseudo || 'Inconnu') : 'Inconnu';

            return { merchantId: mId, pseudo: merchantPseudo, type, multiple, points, compteur };
          } catch (e) {
            console.warn('Skip merchant due to error:', e);
            return null;
          }
        });

        const results = await Promise.allSettled(tasks);
        const out = results.filter(r => r.status === 'fulfilled' && r.value).map(r => r.value);
        out.sort((a, b) => (b.points + b.compteur) - (a.points + a.compteur));
        setMerchants(out);

        if (out.length === 1) {
          setSelectedMerchant(out[0].merchantId);
          setKind(out[0].type === 'Fidbypoints' ? 'points' : 'passages');
          setJustSelected(true);
          setTimeout(() => setJustSelected(false), 200);
        }

        setError('');
      } catch (e) {
        console.error(e);
        setError('Impossible de charger vos comptes fidélité.');
      } finally {
        setLoading(false);
      }
    });

    // cleanup auth + éventuel listener d'offre émetteur
    return () => {
      if (unsubAuth) unsubAuth();
      if (offerUnsubRef.current) { offerUnsubRef.current(); offerUnsubRef.current = null; }
    };
    // eslint-disable-next-line
  }, [toUid, offerParam, modeParam, navigate, goTarget, viewerBase]);

  const selected = merchants.find(m => m.merchantId === selectedMerchant);

  const maxQty = useMemo(() => {
    if (!selected) return 1;
    return kind === 'points' ? Math.max(1, selected.points) : Math.max(1, selected.compteur);
  }, [selected, kind]);

  const onChangeMerchant = (mId) => {
    const m = merchants.find(x => x.merchantId === mId);
    setSelectedMerchant(mId);
    if (m) setKind(m.type === 'Fidbypoints' ? 'points' : 'passages');
    setQty(1);
    setJustSelected(true);
    setTimeout(() => setJustSelected(false), 200);
  };

  const goNext = () => setStep(s => Math.min(4, s + 1));
  const goPrev = () => setStep(s => Math.max(1, s - 1));

  // Générer l’offre et montrer le QR (5 minutes)
  const createOfferAndShowQR = async () => {
    try {
      if (!me) return alert('Connectez-vous.');
      if (!selected) return alert('Choisissez une entreprise.');
      const q = Math.min(Math.max(1, Number(qty || 1)), maxQty);

      setSending(true);
      const expiresAtMs = Date.now() + 5 * 60 * 1000;

      const refTx = await addDoc(collection(firestore, 'transfers'), {
        status: 'offer',
        fromUid: me.uid,
        toUid: null,
        merchantId: selected.merchantId,
        kind,
        qty: q,
        createdAt: serverTimestamp(),
        expiresAtMs,
      });

      setOfferId(refTx.id);
      setOfferExpireAt(expiresAtMs);
      setStep(4);
      setWaitingDone(true);

      // Nettoie un éventuel listener précédent
      if (offerUnsubRef.current) { offerUnsubRef.current(); offerUnsubRef.current = null; }

      // Écoute du statut pour détecter la réussite/expiration
      offerUnsubRef.current = onSnapshot(doc(firestore, 'transfers', refTx.id), (snap) => {
        const d = snap.data();
        if (!d) return;
        if (d.status === 'expired') {
          setWaitingDone(false);
          setKoOverlay(true);
          setTimeout(() => setKoOverlay(false), 1500);
        }
        if (d.status === 'done') {
          setWaitingDone(false);
          setOkOverlay(true);
          setTimeout(() => {
            setOkOverlay(false);
            // 🔁 Redirection vers le viewer pour l’émetteur
            navigate(goViewer(d.merchantId));
          }, 1100);
        }
      });
    } catch (e) {
      console.error(e);
      alert('Erreur lors de la création du QR de transfert.');
    } finally {
      setSending(false);
    }
  };

  // Compte à rebours local
  useEffect(() => {
    if (!offerExpireAt) return;
    const t = setInterval(() => {
      setLeftMs(Math.max(0, offerExpireAt - Date.now()));
    }, 250);
    return () => clearInterval(t);
  }, [offerExpireAt]);

  const mmss = () => {
    const s = Math.floor(leftMs / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(1, '0')}:${String(r).padStart(2, '0')}`;
  };

  const cancelOffer = async () => {
    if (!offerId) return;
    try {
      await updateDoc(doc(firestore, 'transfers', offerId), { status: 'expired' });
    } catch (e) {
      console.error('cancelOffer:', e);
    }
    if (offerUnsubRef.current) { offerUnsubRef.current(); offerUnsubRef.current = null; }
    setWaitingDone(false);
    navigate(goTarget);
  };

  // Finalisation côté client (transaction)
  // ➜ n'efface RIEN. On met à jour les compteurs et on AJOUTE dans transferHistory
  const claimOfferAsReceiver = async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) return alert('Connectez-vous.');
      if (!offerParam) return alert('Offre invalide.');
      setSending(true);

      if (USE_CLIENT_FINALIZE) {
        let merchantIdForRedirect = null;

        await runTransaction(firestore, async (tx) => {
          const txRef = doc(firestore, 'transfers', offerParam);
          const txSnap = await tx.get(txRef);
          if (!txSnap.exists()) throw new Error('Offre introuvable.');
          const t = txSnap.data();

          if (t.status !== 'offer') throw new Error('Offre déjà traitée.');
          if (t.expiresAtMs && Date.now() > t.expiresAtMs) throw new Error('Offre expirée.');
          const { fromUid, merchantId, kind: tKind, qty: tQty } = t || {};
          if (!fromUid || !merchantId || !tKind || !tQty) throw new Error('Offre incomplète.');
          const qty = Number(tQty || 0);
          if (qty <= 0) throw new Error('Quantité invalide.');

          merchantIdForRedirect = merchantId;

          const fromRef = doc(firestore, 'fidelity', merchantId, 'list', fromUid);
          const toRef   = doc(firestore, 'fidelity', merchantId, 'list', user.uid);

          // Lire soldes des deux côtés
          const [fromSnap, toSnap] = await Promise.all([tx.get(fromRef), tx.get(toRef)]);
          if (!fromSnap.exists()) throw new Error('Solde donneur introuvable.');
          const fromData = fromSnap.data() || {};
          const toData   = toSnap.exists() ? (toSnap.data() || {}) : {};

          const fromPts0  = Number(fromData.points || 0);
          const fromPass0 = Number(fromData.compteur || 0);
          const toPts0    = Number(toData.points || 0);
          const toPass0   = Number(toData.compteur || 0);

          let newFromPts  = fromPts0;
          let newFromPass = fromPass0;
          let newToPts    = toPts0;
          let newToPass   = toPass0;

          if (tKind === 'points') {
            if (fromPts0 < qty) throw new Error('Solde insuffisant (points).');
            newFromPts = fromPts0 - qty;
            newToPts   = toPts0 + qty;
          } else {
            if (fromPass0 < qty) throw new Error('Solde insuffisant (passages).');
            newFromPass = fromPass0 - qty;
            newToPass   = toPass0 + qty;
          }

          const base = {
            merchantId,
            kind: tKind,
            qty,
            at: Date.now(), // nombre
          };

          const senderEntry = { ...base, direction: 'out', toUid: user.uid };
          const receiverEntry = { ...base, direction: 'in', fromUid };

          // ➜ Donneur : MAJ compteurs + AJOUT historique (pas de suppression de champs)
          tx.update(fromRef, {
            points: newFromPts,
            compteur: newFromPass,
            transferHistory: arrayUnion(senderEntry),
          });

          // ➜ Receveur : MAJ compteurs + AJOUT historique (pas de migratedFrom)
          if (toSnap.exists()) {
            tx.update(toRef, {
              points: newToPts,
              compteur: newToPass,
              transferHistory: arrayUnion(receiverEntry),
            });
          } else {
            tx.set(toRef, {
              points: newToPts,
              compteur: newToPass,
              transferHistory: [receiverEntry],
            }, { merge: true });
          }

          // Marquer le transfert comme "done"
          tx.update(txRef, {
            toUid: user.uid,
            status: 'done',
            claimedAt: serverTimestamp(),
          });
        });

        setSending(false);
        setOkOverlay(true);
        setTimeout(() => {
          setOkOverlay(false);
          // 🔁 Redirection vers le viewer pour le receveur
          navigate(goViewer(merchantIdForRedirect));
        }, 1100);
      } else {
        // Flux backend : passer en pending et laisser la CF faire pending -> done
        await updateDoc(doc(firestore, 'transfers', offerParam), {
          toUid: user.uid,
          status: 'pending',
          claimedAt: serverTimestamp(),
        });
        setSending(false);
        // on reste à l’écoute via onSnapshot (useEffect claim)
      }
    } catch (e) {
      console.error(e);
      setSending(false);
      alert(e?.message || 'Impossible de réclamer cette offre.');
    }
  };

  const renderStep1 = () => {
    const Card = ({ m, selectedView }) => {
      const isSelected = selectedMerchant === m.merchantId;
      const pop = justSelected && isSelected;

      return (
        <div
          onClick={!selectedView ? () => onChangeMerchant(m.merchantId) : undefined}
          className={`tf-merchant${isSelected ? ' is-selected' : ''}${selectedView ? ' is-static' : ''}${pop ? ' is-pop' : ''}`}
          role={!selectedView ? 'button' : undefined}
          tabIndex={!selectedView ? 0 : undefined}
          onKeyDown={!selectedView ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChangeMerchant(m.merchantId); } } : undefined}
        >
          <div className="tf-merchant__head">
            <MerchantLogo merchantId={m.merchantId} size={48} />
            <div className="tf-merchant__name">{m.pseudo}</div>
          </div>

          <div className="tf-merchant__meta">
            {m.type === 'Fidbypoints' ? (
              <span className="eb-badge eb-badge--success"><strong>{m.points}</strong>&nbsp;pts</span>
            ) : (
              <>
                <span className="eb-badge">
                  <strong>{m.compteur}</strong>&nbsp;passages
                </span>
                {m.multiple ? (
                  <span className="tf-muted-sm">
                    palier {m.multiple - 1} ➜ gain <FaGift style={{ marginLeft: 4, verticalAlign: '-2px' }} />
                  </span>
                ) : null}
              </>
            )}
          </div>
        </div>
      );
    };

    if (selected) {
      return (
        <>
          <h2 className="tf-h2">Étape 1 — Sélection de l’entreprise</h2>
          <div className="tf-narrow">
            <Card m={selected} selectedView />

            <div className="tf-actions">
              <button
                type="button"
                className="eb-btn eb-btn--secondary"
                onClick={() => setSelectedMerchant(null)}
              >
                Changer d’entreprise
              </button>
              <button
                type="button"
                className="eb-btn eb-btn--primary"
                onClick={goNext}
              >
                Continuer
              </button>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <h2 className="tf-h2">Étape 1 — Sélection de l’entreprise</h2>
        {loading ? (
          <div className="eb-card tf-empty"><span className="eb-spinner" aria-hidden="true" /><span>Chargement…</span></div>
        ) : merchants.length === 0 ? (
          <div className="eb-card tf-empty"><span>Aucun solde disponible à transférer.</span></div>
        ) : (
          <div className="tf-grid">
            {merchants.map(m => (
              <Card key={m.merchantId} m={m} />
            ))}
          </div>
        )}
      </>
    );
  };

  const renderStep2 = () => (
    <>
      <h2 className="tf-h2">Étape 2 — Détails du transfert</h2>
      {selected && (
        <div className="eb-card tf-panel">
          <div className="tf-merchant__head">
            <MerchantLogo merchantId={selected.merchantId} size={48} />
            <div className="tf-merchant__name">{selected.pseudo}</div>
          </div>

          <div className="tf-block">
            <div className="tf-label">Type</div>
            <div className="tf-options">
              <label className={`tf-option${kind === 'points' ? ' is-checked' : ''}`} style={radioLabelStyle(selected.type !== 'Fidbypoints')}>
                <input
                  type="radio"
                  name="kind"
                  value="points"
                  checked={kind === 'points'}
                  onChange={() => setKind('points')}
                  disabled={selected.type !== 'Fidbypoints'}
                />
                <span>Points <span className="tf-muted-sm">(disponible&nbsp;: {selected.points})</span></span>
              </label>
              <label className={`tf-option${kind === 'passages' ? ' is-checked' : ''}`} style={radioLabelStyle(selected.type !== 'Fidbycount')}>
                <input
                  type="radio"
                  name="kind"
                  value="passages"
                  checked={kind === 'passages'}
                  onChange={() => setKind('passages')}
                  disabled={selected.type !== 'Fidbycount'}
                />
                <span>Passages <span className="tf-muted-sm">(disponible&nbsp;: {selected.compteur})</span></span>
              </label>
            </div>
            {selected.type !== 'Fidbypoints' && kind === 'points' && (
              <div style={warnText}>Ce commerçant utilise le mode “passages”.</div>
            )}
            {selected.type !== 'Fidbycount' && kind === 'passages' && (
              <div style={warnText}>Ce commerçant utilise le mode “points”.</div>
            )}
          </div>

          <div className="tf-block">
            <label className="tf-label" htmlFor="tf-qty">
              Quantité (max {maxQty})
            </label>
            <div className="tf-qty">
              <input
                id="tf-qty"
                className="eb-input"
                type="number"
                inputMode="numeric"
                min={1}
                max={maxQty}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
              <button type="button" aria-label="Diminuer" onClick={() => setQty(v => Math.max(1, Math.min(maxQty, Number(v || 1) - 1)))} className="eb-btn eb-btn--secondary tf-qty__btn">–</button>
              <button type="button" aria-label="Augmenter" onClick={() => setQty(v => Math.max(1, Math.min(maxQty, Number(v || 1) + 1)))} className="eb-btn eb-btn--secondary tf-qty__btn">+</button>
              <button type="button" onClick={() => setQty(maxQty)} className="eb-btn eb-btn--secondary tf-qty__btn">Max</button>
            </div>
          </div>

          <div className="tf-actions tf-actions--split">
            <button type="button" onClick={goPrev} className="eb-btn eb-btn--secondary">
              Précédent
            </button>
            <button type="button" onClick={goNext} className="eb-btn eb-btn--primary">
              Continuer
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderStep3 = () => (
    <>
      <h2 className="tf-h2">Étape 3 — Récapitulatif</h2>
      {selected && (
        <div className="eb-card tf-panel">
          <div className="tf-merchant__head">
            <MerchantLogo merchantId={selected.merchantId} size={48} />
            <div className="tf-merchant__name">{selected.pseudo}</div>
          </div>

          <dl className="tf-recap">
            <div><dt>Type</dt><dd>{kind}</dd></div>
            <div><dt>Quantité</dt><dd>{Math.min(qty || 1, maxQty)}</dd></div>
            <div><dt>Commerçant</dt><dd>{selected.pseudo}</dd></div>
            <div><dt>Destinataire</dt><dd>à définir via QR</dd></div>
          </dl>

          <div className="tf-actions tf-actions--split">
            <button type="button" onClick={() => navigate(-1)} className="eb-btn eb-btn--ghost">
              Annuler
            </button>
            <div className="tf-actions__group">
              <button type="button" onClick={goPrev} className="eb-btn eb-btn--secondary">
                Modifier
              </button>
              <button type="button" onClick={createOfferAndShowQR} disabled={sending} className="eb-btn eb-btn--primary">
                {sending ? 'Création…' : 'Générer le QR (5 min)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderStep4_QR = () => {
    const origin =
      (typeof window !== 'undefined' && window.location && window.location.origin)
        ? window.location.origin
        : 'https://ecobill.app';

    const qrValue = offerId ? `${origin}/redirectqrctransfertfid/${offerId}` : '';

    // Émetteur : montre le QR + compte à rebours
    if (!offerParam) {
      return (
        <>
          <h2 className="tf-h2">Étape 4 — Partage du QR (5 minutes)</h2>
          <div className="eb-card tf-panel" style={{ textAlign: 'center' }}>
            <p className="tf-lead">Demandez au bénéficiaire de scanner ce QR pour récupérer vos points.</p>
            <div className="tf-qr">
              {offerId ? <QRCodeCanvas value={qrValue} size={220} includeMargin /> : <span className="eb-spinner" aria-hidden="true" />}
            </div>
            <div className={`tf-timer${leftMs === 0 ? ' is-expired' : ''}`}>
              Temps restant : <strong>{mmss()}</strong>
            </div>
            <div className="tf-actions" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(qrValue);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000); // reset après 2s
                }}
                className={`eb-btn ${copied ? 'eb-btn--success' : 'eb-btn--primary'}`}
              >
                {copied ? <FaCheck aria-hidden="true" /> : <FaRegCopy aria-hidden="true" />}
                {copied ? 'Lien copié !' : 'Copier le lien'}
              </button>

              <button type="button" onClick={cancelOffer} className="eb-btn eb-btn--secondary">
                Annuler
              </button>
            </div>
          </div>
          {leftMs === 0 && (
            <div className="eb-alert eb-alert--danger tf-narrow" style={{ marginTop: 12 }} role="alert">
              Offre expirée. Veuillez régénérer un QR.
            </div>
          )}
        </>
      );
    }

    // Receveur : affiche le bouton Récupérer
    const isExpired = offerExpireAt && Date.now() > offerExpireAt;
    return (
      <>
        <h2 className="tf-h2">Récupération de points</h2>
        <div className="eb-card tf-panel" style={{ textAlign: 'center' }}>
          <div className="tf-gift" aria-hidden="true"><FaGift /></div>
          <p className="tf-lead" style={{ fontWeight: 600, color: 'var(--eb-text)' }}>
            Invitation à récupérer des points de fidélité
          </p>
          <div className="tf-actions tf-actions--stack">
            <button
              type="button"
              onClick={claimOfferAsReceiver}
              disabled={sending || isExpired}
              className="eb-btn eb-btn--primary eb-btn--lg"
            >
              {isExpired ? 'Offre expirée' : (sending ? 'Traitement…' : 'Récupérer')}
            </button>
            <button type="button" onClick={() => navigate(goTarget)} className="eb-btn eb-btn--ghost">
              Annuler
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="tf-page">
      <style>{TF_CSS}</style>
      {sending && (<div style={overlayStyle}><span className="eb-spinner eb-spinner--light" style={{ width: 56, height: 56 }} aria-label="Traitement en cours" /></div>)}
      {okOverlay && (<div style={overlayStyle}><span style={{ ...overlayBadge, color: 'var(--eb-success)' }}><FaCheckCircle size={64} /></span></div>)}
      {koOverlay && (<div style={overlayStyle}><span style={{ ...overlayBadge, color: 'var(--eb-danger)' }}><FaTimesCircle size={64} /></span></div>)}

      <div className="tf-container">
        <div className="tf-topbar">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="eb-btn eb-btn--ghost eb-btn--sm"
            style={{ minHeight: 40 }}
          >
            <FaArrowLeft aria-hidden="true" /> Retour
          </button>
        </div>

        <header className="tf-header">
          <h1 className="tf-title">Transfert de fidélité</h1>
          {!!toUid && (
            <p className="tf-subtitle">
              Destinataire : <strong>{receiverPseudo}</strong>
            </p>
          )}
        </header>
        <StepIndicator step={step} />

        <div>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4_QR()}
        </div>

        {error && (<div className="eb-alert eb-alert--danger tf-narrow" style={{ marginTop: 16 }} role="alert">{error}</div>)}
      </div>
    </div>
  );
};

// Styles
const TF_CSS = `
.tf-page { min-height: 100vh; min-height: 100dvh; background: var(--eb-bg); padding: 12px 16px 48px; }
.tf-container { width: 100%; max-width: 760px; margin: 0 auto; }
.tf-topbar { display: flex; align-items: center; margin-left: -8px; }
.tf-header { text-align: center; margin: 8px 0 20px; }
.tf-title { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); }
.tf-subtitle { margin: 6px 0 0; color: var(--eb-muted); font-size: 14.5px; }
.tf-subtitle strong { color: var(--eb-text); font-weight: 600; }

.tf-steps { list-style: none; margin: 0 auto 28px; padding: 0; display: grid; grid-template-columns: repeat(4, 1fr); max-width: 560px; }
.tf-step { position: relative; display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; min-width: 0; }
.tf-step:not(:last-child)::after {
  content: ''; position: absolute; top: 14px; left: calc(50% + 20px); right: calc(-50% + 20px);
  height: 2px; border-radius: 2px; background: var(--eb-border);
}
.tf-step.is-done:not(:last-child)::after { background: var(--eb-success); }
.tf-step__dot {
  position: relative; z-index: 1; width: 28px; height: 28px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; background: var(--eb-surface); color: var(--eb-muted);
  border: 1.5px solid var(--eb-border-strong);
}
.tf-step.is-active .tf-step__dot { background: var(--eb-brand); border-color: var(--eb-brand); color: #fff; box-shadow: var(--eb-ring); }
.tf-step.is-done .tf-step__dot { background: var(--eb-success); border-color: var(--eb-success); color: #fff; }
.tf-step__label { font-size: 13px; color: var(--eb-muted); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
.tf-step.is-active .tf-step__label { color: var(--eb-brand); font-weight: 600; }

.tf-h2 { margin: 0 0 14px; text-align: center; font-size: 17px; font-weight: 650; color: var(--eb-text-2); }
.tf-narrow { max-width: 560px; margin-left: auto; margin-right: auto; }
.tf-panel { max-width: 560px; margin: 0 auto; padding: 20px; }
.tf-lead { margin: 0 0 4px; color: var(--eb-text-2); font-size: 15px; line-height: 1.5; }
.tf-muted-sm { font-size: 13px; color: var(--eb-muted); font-weight: 400; }
.tf-empty { max-width: 560px; margin: 0 auto; padding: 32px 20px; display: flex; flex-direction: column; align-items: center; gap: 14px; color: var(--eb-muted); text-align: center; }

.tf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr)); gap: 12px; }
.tf-merchant {
  background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg);
  padding: 16px; box-shadow: var(--eb-shadow-xs); cursor: pointer; text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.16s ease;
}
.tf-merchant:hover:not(.is-static) { border-color: var(--eb-brand-300); box-shadow: var(--eb-shadow-md); }
.tf-merchant:focus-visible { outline: none; box-shadow: var(--eb-ring); }
.tf-merchant.is-selected { border-color: var(--eb-brand); box-shadow: 0 0 0 1px var(--eb-brand) inset, var(--eb-shadow-sm); }
.tf-merchant.is-static { cursor: default; }
.tf-merchant.is-pop { transform: scale(1.02); }
.tf-merchant__head { display: flex; align-items: center; gap: 12px; min-width: 0; }
.tf-merchant__name { font-weight: 600; font-size: 16px; color: var(--eb-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tf-merchant__meta { margin-top: 14px; display: flex; gap: 8px; align-items: center; justify-content: space-between; flex-wrap: wrap; }

.tf-block { margin-top: 20px; }
.tf-label { display: block; margin-bottom: 8px; font-size: 13.5px; font-weight: 600; color: var(--eb-text-2); }
.tf-options { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 8px; }
.tf-option {
  min-height: 48px; padding: 10px 14px; border-radius: var(--eb-radius-sm);
  border: 1px solid var(--eb-border-strong); background: var(--eb-surface); font-size: 14.5px; color: var(--eb-text);
}
.tf-option input { width: 18px; height: 18px; margin: 0; flex-shrink: 0; }
.tf-option.is-checked { border-color: var(--eb-brand); background: var(--eb-brand-50); }
.tf-qty { display: flex; gap: 8px; }
.tf-qty .eb-input { flex: 1 1 auto; min-width: 0; font-size: 16px; }
.tf-qty__btn { min-width: 46px; min-height: 46px; padding: 0 12px; font-size: 16px; }

.tf-recap { margin: 18px 0 0; padding: 4px 16px; border-radius: var(--eb-radius); background: var(--eb-surface-2); border: 1px solid var(--eb-border); }
.tf-recap > div { display: flex; justify-content: space-between; gap: 16px; padding: 10px 0; border-bottom: 1px solid var(--eb-border); font-size: 14.5px; }
.tf-recap > div:last-child { border-bottom: 0; }
.tf-recap dt { color: var(--eb-muted); }
.tf-recap dd { margin: 0; font-weight: 600; color: var(--eb-text); text-align: right; min-width: 0; overflow-wrap: anywhere; }

.tf-actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; margin-top: 20px; }
.tf-actions--split { justify-content: space-between; }
.tf-actions__group { display: flex; flex-wrap: wrap; gap: 10px; }
.tf-actions--stack { flex-direction: column; align-items: stretch; max-width: 320px; margin-left: auto; margin-right: auto; }

.tf-qr { display: flex; justify-content: center; align-items: center; min-height: 236px; margin: 16px auto 0; padding: 8px; width: fit-content; background: #fff; border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); }
.tf-qr canvas { display: block; max-width: 100%; height: auto !important; }
.tf-timer { margin-top: 16px; font-size: 15px; color: var(--eb-muted); }
.tf-timer strong { display: block; margin-top: 2px; font-size: 28px; font-weight: 700; color: var(--eb-text); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
.tf-timer.is-expired strong { color: var(--eb-danger); }
.tf-gift { width: 56px; height: 56px; margin: 4px auto 14px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: var(--eb-brand-50); border: 1px solid var(--eb-brand-100); color: var(--eb-brand); font-size: 24px; }

@media (max-width: 480px) {
  .tf-title { font-size: 22px; }
  .tf-step__label { font-size: 12px; }
  .tf-panel { padding: 18px 16px; }
  .tf-actions, .tf-actions--split { flex-direction: column-reverse; align-items: stretch; }
  .tf-actions .eb-btn, .tf-actions__group { width: 100%; }
  .tf-actions__group { flex-direction: column-reverse; }
  .tf-actions--stack { flex-direction: column; }
}
`;

const overlayStyle = {
  position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 3000, background: 'rgba(15,35,56,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', color: '#fff'
};

const overlayBadge = {
  width: 104, height: 104, borderRadius: '50%', background: 'var(--eb-surface)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--eb-shadow-lg)'
};

const radioLabelStyle = (disabled) => ({
  display: 'flex', alignItems: 'center', gap: 10,
  opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
  userSelect: 'none'
});

const warnText = { color: 'var(--eb-warning)', fontSize: 13, marginTop: 8 };

export default TransfertFid;
